import React, { useState, useEffect } from 'react';
import { listingTableSx } from './listingTableSx';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Avatar,
  Tooltip,
  InputAdornment,
  Divider,
  Skeleton,
} from '@mui/material';
import {
  Search,
  Refresh,
  Visibility,
  Person,
  Business,
  Security,
  Assignment,
  CheckCircle,
  Cancel,
  Edit,
  Delete,
  Add,
  Info,
  Close as CloseIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import auditService, { AuditEvent, AuditFilters } from '../services/auditService';
import { auditHumanTitle, auditRecordPath, auditStoredPath, sortAuditEvents } from '../utils/auditTitle';
import { ListingPagination, ListingSortSelect } from './ListingPagination';
import { DEFAULT_LISTING_SORT, ListingSortValue, listingSortToAuditQuery } from '../utils/listingSort';
import { chipSxFromSurface, getRiskSurface } from '../utils/appearanceTokens';
import type { PaletteMode } from '@mui/material';

// ─── helpers ──────────────────────────────────────────────────────────────────

function getEventIcon(type: string) {
  const sz = { fontSize: '1rem' };
  switch (type) {
    case 'create':   return <Add color="success" sx={sz} />;
    case 'update':   return <Edit color="primary" sx={sz} />;
    case 'delete':   return <Delete color="error" sx={sz} />;
    case 'approve':  return <CheckCircle color="success" sx={sz} />;
    case 'reject':   return <Cancel color="error" sx={sz} />;
    case 'submit':   return <Assignment color="info" sx={sz} />;
    case 'review':   return <Visibility color="warning" sx={sz} />;
    case 'delegate': return <Person color="secondary" sx={sz} />;
    case 'complete': return <CheckCircle color="success" sx={sz} />;
    case 'login':    return <Person color="primary" sx={sz} />;
    case 'revoke':   return <Cancel color="warning" sx={sz} />;
    default:         return <Info color="inherit" sx={sz} />;
  }
}

function getEntityIcon(type: string) {
  const sz = { fontSize: '0.9rem' };
  switch (type) {
    case 'application':      return <Business sx={sz} />;
    case 'user':             return <Person sx={sz} />;
    case 'sso_config':       return <Security sx={sz} />;
    case 'delegation':       return <Person sx={sz} />;
    case 'approval_request': return <Assignment sx={sz} />;
    case 'entitlement':      return <Security sx={sz} />;
    case 'branding':         return <Business sx={sz} />;
    case 'provider':         return <Security sx={sz} />;
    case 'risk_assessment':  return <Assignment sx={sz} />;
    case 'team_contact':     return <Person sx={sz} />;
    case 'pam_account':      return <Security sx={sz} />;
    case 'ai_asset':         return <Assignment sx={sz} />;
    case 'ai_access_grant':  return <Security sx={sz} />;
    case 'ai_capability':
    case 'ai_tool':
    case 'ai_data_source':
    case 'ai_prompt':        return <Assignment sx={sz} />;
    default:                 return <Info sx={sz} />;
  }
}

function criticalityChipSx(level: string, mode: PaletteMode): object {
  return {
    ...chipSxFromSurface(getRiskSurface(level, mode)),
    fontWeight: ['critical', 'high'].includes(String(level || '').toLowerCase()) ? 700 : 600,
  };
}

const HEADER_SX = {
  fontSize: '0.7rem',
  fontWeight: 700,
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  letterSpacing: 0.5,
  py: 1.5,
  whiteSpace: 'nowrap' as const,
};

// ─── component ────────────────────────────────────────────────────────────────

const AuditTrail: React.FC = () => {
  const { user } = useAuth();
  const { colors, resolvedMode } = useTheme();
  const [searchParams] = useSearchParams();

  const [events, setEvents]           = useState<AuditEvent[]>([]);
  const [loading, setLoading]         = useState(true);
  const [selected, setSelected]       = useState<AuditEvent | null>(null);
  const [error, setError]             = useState('');
  const [page, setPage]               = useState(1);
  const [listingSort, setListingSort] = useState<ListingSortValue>(DEFAULT_LISTING_SORT);
  const [totalPages, setTotalPages]   = useState(1);
  const [totalCount, setTotalCount]   = useState(0);
  const [filters, setFilters]         = useState<AuditFilters>({
    event_type: '', entity_type: '', user_role: '',
    date_from: '', date_to: '', search: '',
    criticality_level: '', page: 1, limit: 20,
  });

  useEffect(() => {
    const preset = searchParams.get('entity_type');
    if (preset) {
      setFilters((prev) => ({ ...prev, entity_type: preset }));
      setPage(1);
    }
  }, [searchParams]);

  useEffect(() => { load(); }, [page, filters, listingSort]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await auditService.getAuditEvents({ ...filters, page, limit: 20, ...listingSortToAuditQuery(listingSort) });
      setEvents(sortAuditEvents(result.events));
      setTotalCount(result.total);
      setTotalPages(Math.ceil(result.total / 20));
    } catch {
      setError('Failed to load audit events.');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const setFilter = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ event_type: '', entity_type: '', user_role: '', date_from: '', date_to: '', search: '', criticality_level: '', page: 1, limit: 20 });
    setPage(1);
  };

  const fmtTime = (ts: string) =>
    new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assignment sx={{ color: 'primary.main', fontSize: 22 }} />
          <Box>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.2 }}>Audit Trail</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
              Detailed log of sign-ins, configuration changes, approvals, and other system activity. Duplicate entries are merged automatically.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={async () => {
            const end = new Date();
            const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
            const signed = await auditService.exportSigned(start.toISOString(), end.toISOString());
            const blob = new Blob(
              [`${signed.csv}\n\n# ${signed.algorithm} ${signed.signature}\n`],
              { type: 'text/csv' },
            );
            const url = URL.createObjectURL(blob);
            const link = window.document.createElement('a');
            link.href = url;
            link.download = 'audit-export.csv';
            link.click();
            URL.revokeObjectURL(url);
          }}
        >
          Export signed week
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Refresh />}
          onClick={load}
          disabled={loading}
          sx={{ fontSize: '0.78rem', py: 0.5 }}
        >
          Refresh
        </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── Filters ── */}
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
          <FilterListIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Filters
          </Typography>
        </Box>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth size="small" label="Search"
              value={filters.search || ''}
              onChange={e => setFilter('search', e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: '1rem' }} /></InputAdornment> }}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.82rem' } }}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small" sx={{ '& .MuiInputLabel-root': { fontSize: '0.82rem' }, '& .MuiSelect-select': { fontSize: '0.82rem' } }}>
              <InputLabel>Event Type</InputLabel>
              <Select value={filters.event_type || ''} onChange={e => setFilter('event_type', e.target.value as string)} label="Event Type">
                <MenuItem value="">All</MenuItem>
                {['create','update','delete','approve','reject','submit','review','delegate','complete','login','revoke'].map(v =>
                  <MenuItem key={v} value={v} sx={{ textTransform: 'capitalize', fontSize: '0.82rem' }}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small" sx={{ '& .MuiInputLabel-root': { fontSize: '0.82rem' }, '& .MuiSelect-select': { fontSize: '0.82rem' } }}>
              <InputLabel>Entity</InputLabel>
              <Select value={filters.entity_type || ''} onChange={e => setFilter('entity_type', e.target.value as string)} label="Entity">
                <MenuItem value="">All</MenuItem>
                <MenuItem value="application" sx={{ fontSize: '0.82rem' }}>Application</MenuItem>
                <MenuItem value="sso_config" sx={{ fontSize: '0.82rem' }}>SSO Config</MenuItem>
                <MenuItem value="user" sx={{ fontSize: '0.82rem' }}>User</MenuItem>
                <MenuItem value="delegation" sx={{ fontSize: '0.82rem' }}>Delegation</MenuItem>
                <MenuItem value="approval_request" sx={{ fontSize: '0.82rem' }}>Approval</MenuItem>
                <MenuItem value="entitlement" sx={{ fontSize: '0.82rem' }}>Entitlement</MenuItem>
                <MenuItem value="branding" sx={{ fontSize: '0.82rem' }}>Branding</MenuItem>
                <MenuItem value="provider" sx={{ fontSize: '0.82rem' }}>Provider</MenuItem>
                <MenuItem value="risk_assessment" sx={{ fontSize: '0.82rem' }}>Risk Assessment</MenuItem>
                <MenuItem value="team_contact" sx={{ fontSize: '0.82rem' }}>Team Contact</MenuItem>
                <MenuItem value="ai_asset" sx={{ fontSize: '0.82rem' }}>AI Asset</MenuItem>
                <MenuItem value="ai_access_grant" sx={{ fontSize: '0.82rem' }}>AI Access Grant</MenuItem>
                <MenuItem value="ai_capability" sx={{ fontSize: '0.82rem' }}>AI Capability</MenuItem>
                <MenuItem value="ai_tool" sx={{ fontSize: '0.82rem' }}>AI Tool</MenuItem>
                <MenuItem value="ai_data_source" sx={{ fontSize: '0.82rem' }}>AI Data Source</MenuItem>
                <MenuItem value="ai_prompt" sx={{ fontSize: '0.82rem' }}>AI Prompt</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small" sx={{ '& .MuiInputLabel-root': { fontSize: '0.82rem' }, '& .MuiSelect-select': { fontSize: '0.82rem' } }}>
              <InputLabel>Criticality</InputLabel>
              <Select value={filters.criticality_level || ''} onChange={e => setFilter('criticality_level', e.target.value as string)} label="Criticality">
                <MenuItem value="">All</MenuItem>
                {['low','medium','high','critical'].map(v =>
                  <MenuItem key={v} value={v} sx={{ textTransform: 'capitalize', fontSize: '0.82rem' }}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <ListingSortSelect
              value={listingSort}
              onChange={(value) => { setListingSort(value); setPage(1); }}
              sx={{ '& .MuiInputLabel-root': { fontSize: '0.82rem' }, '& .MuiSelect-select': { fontSize: '0.82rem' } }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField fullWidth size="small" label="From" type="date" value={filters.date_from || ''}
              onChange={e => setFilter('date_from', e.target.value)} InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.82rem' } }} />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField fullWidth size="small" label="To" type="date" value={filters.date_to || ''}
              onChange={e => setFilter('date_to', e.target.value)} InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.82rem' } }} />
          </Grid>
          <Grid item xs={6} sm={2} md={1}>
            <Button variant="outlined" size="small" onClick={clearFilters} sx={{ height: 40, fontWeight: 600, fontSize: '0.82rem' }} fullWidth>
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Table ── */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" fontWeight={600} color="text.secondary">
            {loading ? 'Loading…' : `${totalCount.toLocaleString()} event${totalCount !== 1 ? 's' : ''}`}
          </Typography>
        </Box>

        <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)', overflowX: 'hidden' }}>
          <Table stickyHeader size="small" sx={{ ...listingTableSx, tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                {[
                  { label: 'Event',       width: '10%' },
                  { label: 'Entity',      width: '10%' },
                  { label: 'User',        width: '15%' },
                  { label: 'Description', width: '31%' },
                  { label: 'Criticality', width: '9%'  },
                  { label: 'Timestamp',   width: '17%' },
                  { label: '',            width: '8%'  },
                ].map(({ label, width }) => (
                  <TableCell key={label} sx={{ ...HEADER_SX, width, bgcolor: 'grey.50' }}>
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(7)].map((__, j) => (
                      <TableCell key={j}><Skeleton height={26} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">No audit events found</Typography>
                    <Typography variant="caption" color="text.disabled">Try adjusting your filters</Typography>
                  </TableCell>
                </TableRow>
              ) : events.map((ev, idx) => (
                <TableRow
                  key={ev.id}
                  hover
                  sx={{ bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.50', '&:hover': { bgcolor: 'action.hover' } }}
                >
                  {/* Event type */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {getEventIcon(ev.event_type)}
                      <Typography sx={{ fontSize: '0.78rem', textTransform: 'capitalize', fontWeight: 500 }}>
                        {ev.event_type}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Entity */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {getEntityIcon(ev.entity_type)}
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', textTransform: 'capitalize' }} noWrap>
                        {ev.entity_type.replace(/_/g, ' ')}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* User */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Avatar sx={{ width: 22, height: 22, fontSize: '0.65rem', bgcolor: colors.primary }}>
                        {(ev.user_name || '?').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 500 }} noWrap>{ev.user_name || '—'}</Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', textTransform: 'capitalize' }} noWrap>
                          {(ev.user_role || '').replace(/_/g, ' ')}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Description */}
                  <TableCell>
                    <Typography sx={{ fontSize: '0.78rem' }} noWrap title={auditHumanTitle(ev)}>
                      {auditHumanTitle(ev)}
                    </Typography>
                    {ev.entity_name && (
                      <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }} noWrap>
                        {ev.entity_name}
                      </Typography>
                    )}
                  </TableCell>

                  {/* Criticality */}
                  <TableCell>
                    <Chip
                      label={ev.criticality_level || 'medium'}
                      size="small"
                      sx={{ ...criticalityChipSx(ev.criticality_level || 'medium', resolvedMode), fontSize: '0.68rem', height: 18, textTransform: 'capitalize', '& .MuiChip-label': { px: 0.75 } }}
                    />
                  </TableCell>

                  {/* Timestamp */}
                  <TableCell>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {fmtTime(ev.timestamp)}
                    </Typography>
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="center">
                    <Tooltip title="View details">
                      <IconButton size="small" onClick={() => setSelected(ev)} sx={{ p: 0.5 }}>
                        <Visibility sx={{ fontSize: '0.95rem' }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <ListingPagination
          page={page}
          totalPages={totalPages}
          totalItems={totalCount}
          pageSize={20}
          onPageChange={setPage}
        />
      </Paper>

      {/* ── Detail Dialog ── */}
      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, px: 2.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selected && getEventIcon(selected.event_type)}
            <Typography fontWeight={700} fontSize="0.95rem">{selected ? auditHumanTitle(selected) : 'Event details'}</Typography>
          </Box>
          <IconButton size="small" onClick={() => setSelected(null)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>

        {selected && (
          <DialogContent sx={{ p: 2.5 }}>
            <Grid container spacing={2}>
              {[
                { label: 'Event Type',  value: selected.event_type,                  cap: true },
                { label: 'Entity Type', value: selected.entity_type.replace(/_/g,' '), cap: true },
                { label: 'User',        value: `${selected.user_name} (${(selected.user_role||'').replace(/_/g,' ')})` },
                { label: 'Timestamp',   value: fmtTime(selected.timestamp) },
              ].map(({ label, value, cap }) => (
                <Grid item xs={6} key={label}>
                  <Typography variant="caption" color="text.secondary" display="block" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.25 }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={cap ? { textTransform: 'capitalize' } : {}}>
                    {value}
                  </Typography>
                </Grid>
              ))}

              <Grid item xs={12}>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="caption" color="text.secondary" display="block" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5 }}>
                  Description
                </Typography>
                <Typography variant="body2">{selected.action_description}</Typography>
              </Grid>

              {selected.entity_name && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>Entity</Typography>
                  <Typography variant="body2">{selected.entity_name}</Typography>
                </Grid>
              )}

              {selected.approval_status && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>Approval Status</Typography>
                  <Chip label={selected.approval_status} size="small" sx={{ textTransform: 'capitalize', mt: 0.5, display: 'block', width: 'fit-content' }} />
                </Grid>
              )}

              {selected.ip_address && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>IP Address</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{selected.ip_address}</Typography>
                </Grid>
              )}

              {selected.delegated_to_name && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>Delegated To</Typography>
                  <Typography variant="body2">{selected.delegated_to_name}</Typography>
                </Grid>
              )}

              {selected.comments && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>Comments</Typography>
                  <Typography variant="body2">{selected.comments}</Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>API path</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {String(selected.details?.method || '')} {auditStoredPath(selected) || 'Path is not stored on this event'}
                </Typography>
                {selected.details?.status_code != null && (
                  <Typography variant="body2">Status {String(selected.details.status_code)}</Typography>
                )}
              </Grid>

              {(selected.previous_values || selected.new_values) && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>Before and after</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify({ before: selected.previous_values || null, after: selected.new_values || null }, null, 2)}
                  </Typography>
                </Grid>
              )}

              {auditRecordPath(selected) && (
                <Grid item xs={12}>
                  <Button component={RouterLink} to={auditRecordPath(selected) || '/audit-trail'} size="small">
                    Open the record
                  </Button>
                </Grid>
              )}

              {selected.details && Object.keys(selected.details).length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ mb: 1 }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>Details</Typography>
                  <Paper variant="outlined" sx={{ mt: 0.75, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                      {JSON.stringify(selected.details, null, 2)}
                    </pre>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </DialogContent>
        )}

        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setSelected(null)} variant="contained" size="small" sx={{ bgcolor: colors.primary, fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditTrail;
